// ✅ MINIMAL CHANGES: Only fix the saveStepData calls

import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@shared/ui/button";
import { ChevronRight } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { StepHelpers } from "@shared/components/Stepper";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@shared/hooks/use-toast";
import { useNotificationChoices } from "@features/ux/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useRecipientLists } from "@features/ux/messaging/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useCommunicationEventTypes,
  useUpdateWorkflowTrigger,
} from "@features/ux/work-flow/hooks";

interface WorkflowTriggerStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  existingWorkflow?: any;
  isEditMode?: boolean;
  createdWorkOrderID: number;
}

const WorkflowTriggerStep: React.FC<WorkflowTriggerStepProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  readOnly = false,
  existingWorkflow,
  isEditMode,
  createdWorkOrderID,
}) => {
  const { toast } = useToast();
  const dataLoadedRef = useRef(false);
  const utilityId = useRemoteUtilityId();

  // ... [Keep all existing schema, form setup, and data fetching logic unchanged] ...

  const workflowTriggerSchema = useMemo(
    () =>
      z
        .object({
          triggerType: z.enum(["time-based", "event-based"]),
          date: z.string().optional(),
          time: z.string().optional(),
          eventType: z.string().optional(),
          audienceType: z.string().optional(),
          audienceListId: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (isEditMode) return;

          if (data.triggerType === "time-based") {
            if (!data.date) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Date is required for time-based triggers",
                path: ["date"],
              });
            } else {
              const selectedDate = new Date(data.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              if (selectedDate < today) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Please select a date from today onwards",
                  path: ["date"],
                });
              }
            }
            if (!data.time) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Time is required for time-based triggers",
                path: ["time"],
              });
            }
          } else if (data.triggerType === "event-based") {
            if (!data.eventType) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Event type is required for event-based triggers",
                path: ["eventType"],
              });
            }
          }

          if (data.triggerType === "time-based" && !data.audienceType) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Audience type is required",
              path: ["audienceType"],
            });
          }
          if (data.triggerType === "time-based" && !data.audienceListId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please select recipient list",
              path: ["audienceListId"],
            });
          }
        }),
    [isEditMode]
  );

  const updateTriggerMutation = useUpdateWorkflowTrigger();

  const eventTypesQuery = useCommunicationEventTypes();
  const eventTypes = eventTypesQuery.data || [];
  const isLoadingEventTypes = eventTypesQuery.isLoading;

  const { data: audienceTypes } = useNotificationChoices("recipient_type");
  const audienceTypeChoices = mapToLabelValue(audienceTypes);

  const workflowTriggerForm = useForm<z.infer<typeof workflowTriggerSchema>>({
    resolver: zodResolver(workflowTriggerSchema),
    defaultValues: {
      triggerType: "time-based",
      date: "",
      time: "",
      eventType: "",
      audienceType: "",
      audienceListId: "",
    },
    mode: "onChange",
  });

  const triggerType = workflowTriggerForm.watch("triggerType");
  const currentAudienceType = workflowTriggerForm.watch("audienceType");

  const { data: recipientLists } = useRecipientLists({
    recipientType: currentAudienceType,
    remoteUtilityId: utilityId,
  });

  const filteredRecipientList = recipientLists?.result?.filter(
    (item) => item.isActive == true && item.isDraft == false
  );

  const recipientListChoices = mapToLabelValue(
    filteredRecipientList,
    "name",
    "id"
  );

  // ✅ FIX 1: ONLY save step data in controlled ways - SAME AS NewList
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      const stepData = {
        ...data,
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
      };

      // ✅ Only save in controlled scenarios - NOT during render
      stepHelpers.setStepData(currentStepIndex, stepData);
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      toast({
        title: "",
        description: error?.message,
        variant: "destructive",
      });
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  const parseDateFromAPI = (dateStr: string) => {
    if (!dateStr) return { date: "", time: "00:00" };

    try {
      if (
        dateStr.includes(" ") &&
        !dateStr.includes("T") &&
        !dateStr.includes("-")
      ) {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          return {
            date: parsedDate.toISOString().split("T")[0],
            time: "00:00",
          };
        }
      }

      if (dateStr.includes("T")) {
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) {
          return {
            date: isoDate.toISOString().split("T")[0],
            time: isoDate.toTimeString().slice(0, 5),
          };
        }
      }

      if (dateStr.includes("-")) {
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          if (
            parts[0].length <= 2 &&
            parts[1].length <= 2 &&
            parts[2].length === 4
          ) {
            const [day, month, year] = parts;
            return {
              date: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
              time: "00:00",
            };
          } else {
            return {
              date: dateStr,
              time: "00:00",
            };
          }
        }
      }

      const fallbackDate = new Date(dateStr);
      if (!isNaN(fallbackDate.getTime())) {
        return {
          date: fallbackDate.toISOString().split("T")[0],
          time: "00:00",
        };
      }
    } catch (error) {
      console.error("Date parsing error:", error);
    }

    return { date: "", time: "00:00" };
  };

  // ✅ FIX 2: Data loading with proper guards - SAME PATTERN AS NewList
  useEffect(() => {
    const hasStepHelpersData =
      stepHelpers && Object.keys(getStepData() || {}).length > 0;
    const isFirstTimeEditMode =
      isEditMode && existingWorkflow?.id && !dataLoadedRef.current;

    const shouldLoadData =
      !dataLoadedRef.current && (hasStepHelpersData || isFirstTimeEditMode);

    if (shouldLoadData) {
      try {
        let savedStepData = {};
        let dataSource = "none";

        if (stepHelpers) {
          const stepHelpersData = getStepData() || {};
          const isStepHelpersComplete =
            stepHelpersData.triggerType === "event-based"
              ? stepHelpersData.eventType &&
                stepHelpersData.eventType !== "" &&
                stepHelpersData.audienceType
              : stepHelpersData.triggerType === "time-based"
              ? stepHelpersData.date &&
                stepHelpersData.time &&
                stepHelpersData.audienceType
              : false;

          if (
            Object.keys(stepHelpersData).length > 0 &&
            isStepHelpersComplete
          ) {
            savedStepData = stepHelpersData;
            dataSource = "stepHelpers";
          }
        }

        if (
          (Object.keys(savedStepData).length === 0 &&
            isEditMode &&
            existingWorkflow?.id) ||
          createdWorkOrderID
        ) {
          const storageKey = `workflow-builder-progress-${existingWorkflow.id}`;
          const progressData = localStorage.getItem(storageKey);
          if (progressData) {
            const parsed = JSON.parse(progressData);
            const localStorageData = parsed.stepData?.[currentStepIndex] || {};
            if (Object.keys(localStorageData).length > 0) {
              savedStepData = localStorageData;
              dataSource = "localStorage";
            }
          }
        }

        if (
          Object.keys(savedStepData).length === 0 &&
          existingWorkflow &&
          isEditMode
        ) {
          const triggerType =
            existingWorkflow.triggerType === 0 ? "event-based" : "time-based";

          const { date: processedDate, time: processedTime } = parseDateFromAPI(
            existingWorkflow.startDate
          );

          savedStepData = {
            triggerType,
            eventType: existingWorkflow.eventType || "",
            date: processedDate,
            time: processedTime,
            audienceType: existingWorkflow.recipientType || "",
            audienceListId: existingWorkflow.recipientList?.toString() || "",
            workflowId: existingWorkflow.id,
            timestamp: new Date().toISOString(),
            stepIndex: currentStepIndex,
          };
        }

        if (savedStepData && Object.keys(savedStepData).length > 0) {
          const formData = {
            triggerType: savedStepData.triggerType || "time-based",
            eventType: savedStepData.eventType || "",
            date: savedStepData.date || "",
            time: savedStepData.time || "00:00",
            audienceType: savedStepData.audienceType || "",
            audienceListId: savedStepData.audienceListId || "",
          };

          const currentFormValues = workflowTriggerForm.getValues();
          const shouldUpdateForm =
            currentFormValues.triggerType !== formData.triggerType ||
            currentFormValues.eventType !== formData.eventType ||
            currentFormValues.date !== formData.date ||
            currentFormValues.time !== formData.time ||
            currentFormValues.audienceType !== formData.audienceType ||
            currentFormValues.audienceListId !== formData.audienceListId;

          if (shouldUpdateForm) {
            workflowTriggerForm.reset(formData);
          }

          // ✅ ONLY save if data source is NOT stepHelpers - SAME AS NewList
          if (stepHelpers && dataSource !== "stepHelpers") {
            saveStepData(savedStepData);
          }
        }

        dataLoadedRef.current = true;
      } catch (error) {
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    workflowTriggerForm,
    getStepData,
    isEditMode,
    existingWorkflow?.id,
    existingWorkflow?.recipientType,
    existingWorkflow?.recipientList,
    createdWorkOrderID,
    existingWorkflow?.triggerType,
    existingWorkflow?.eventType,
    existingWorkflow?.startDate,
    saveStepData,
    isLoadingEventTypes,
  ]);

  // ... [Keep all existing form fields, options, and helper functions] ...

  const eventTypeOptions = useMemo(() => {
    if (isLoadingEventTypes) {
      return [{ value: "", label: "Loading event types..." }];
    }

    if (!eventTypes || eventTypes.length === 0) {
      return [{ value: "", label: "No event types available" }];
    }

    const options = eventTypes.map((eventType: string) => ({
      value: eventType,
      label: eventType
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    }));

    return options;
  }, [eventTypes, isLoadingEventTypes]);

  // UPDATED: Form fields with audience/recipient fields
  const workflowTriggerFormFields: FormField[] = useMemo(() => {
    const currentFormValues = workflowTriggerForm.getValues();
    const stepData = getStepData();

    return [
      // Trigger Type Section
      {
        name: "triggerType",
        label: "Trigger Type",
        type: "select",
        required: !isEditMode,
        options: [
          { value: "time-based", label: "Time-Based" },
          { value: "event-based", label: "Event-Based" },
        ],
        helperText: "Choose how this workflow should be triggered",
        showHelperTooltip: true,
        tooltipText:
          "Time-based triggers run at specific times, while event-based triggers respond to system events.",
        disabled: readOnly,
        className: "col-span-6",
        classes: {
          container: "w-full mb-6",
          label: "block text-base font-semibold text-gray-800 mb-2",
          select: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        fullWidth: true,
      },

      // Trigger-specific fields
      ...(triggerType === "event-based"
        ? [
            {
              name: "eventType",
              label: "Event Type",
              type: "select" as const,
              required: triggerType === "event-based",
              options: eventTypeOptions,
              placeholder: isLoadingEventTypes
                ? "Loading event types..."
                : "Select an event type",
              helperText:
                "Choose the system event that will trigger this workflow",
              showHelperTooltip: true,
              tooltipText:
                "Select the specific event in your system that should automatically trigger this workflow.",
              disabled: readOnly || isLoadingEventTypes,
              loading: isLoadingEventTypes,
              className: "col-span-6",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
                  readOnly || isLoadingEventTypes
                    ? "bg-gray-50 cursor-not-allowed"
                    : "bg-white"
                }`,
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
              fullWidth: true,
            },
          ]
        : []),

      ...(triggerType === "time-based"
        ? [
            {
              name: "date",
              label: "Date",
              type: "datepicker" as const,
              required: triggerType === "time-based",
              helperText: "Select the date when the workflow should trigger",
              disabled: readOnly,
              className: "col-span-3",
              customValidation: (value: any) => {
                if (!value) return true; // Allow empty if not required

                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (selectedDate < today) {
                  return "Please select a date from today onwards";
                }
                return true;
              },
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },
            {
              name: "time",
              label: "Time",
              type: "time" as const,
              required: triggerType === "time-based",
              helperText: "Select the time when the workflow should trigger",
              disabled: readOnly,
              className: "col-span-3",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },
            {
              name: "audienceType",
              label: "Audience Type",
              type: "select",
              required: true,
              options: audienceTypeChoices,
              placeholder: "Select audience type",
              helperText: "Choose the target audience for this workflow",
              disabled: readOnly,
              className: "col-span-3",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
                  readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
                }`,
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },

            {
              name: "audienceListId",
              label: "Recipient List",
              type: "select",
              required: true,
              options: recipientListChoices,
              placeholder: "Select a recipient list",
              helperText:
                "Optionally select a specific recipient list for targeted messaging",
              disabled: readOnly || !currentAudienceType,
              showWhen: () => currentAudienceType !== "",
              className: "col-span-3",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
                  readOnly || !currentAudienceType
                    ? "bg-gray-50 cursor-not-allowed"
                    : "bg-white"
                }`,
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },
          ]
        : []),

      // NEW: Audience Configuration Section
    ];
  }, [
    triggerType,
    readOnly,
    eventTypeOptions,
    isLoadingEventTypes,
    isEditMode,
    workflowTriggerForm,
    getStepData,
    existingWorkflow?.eventType,
    existingWorkflow?.startDate,
    audienceTypeChoices,
    recipientListChoices,
    currentAudienceType,
  ]);

  // Rest of the component logic remains the same...
  // (getWorkflowId, buildTriggerData, updateTriggerAPI, handleFormSubmit, etc.)

  const getWorkflowId = (): string | null => {
    const primaryId = existingWorkflow?.id || createdWorkOrderID;
    if (primaryId) {
      return primaryId;
    }

    if (stepHelpers) {
      try {
        const step0Data = stepHelpers.getStepData(0);
        if (step0Data?.workflowId) {
          return step0Data.workflowId;
        }
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Error accessing step data",
          variant: "destructive",
        });
      }
    }

    try {
      const storageKey = "workflow-builder-progress";
      const progressData = localStorage.getItem(storageKey);

      if (progressData) {
        const parsed = JSON.parse(progressData);
        return parsed.stepData?.[0]?.workflowId || null;
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Error accessing stored data",
        variant: "destructive",
      });
    }

    return null;
  };

  const buildTriggerData = (
    formData: z.infer<typeof workflowTriggerSchema>
  ) => {
    if (formData.triggerType === "time-based") {
      const timeBasedData = buildTimeBasedTrigger(formData);
      if (!timeBasedData) return null;

      return {
        recipientType: formData.audienceType,
        recipientList: formData.audienceListId
          ? parseInt(formData.audienceListId)
          : null,
        ...timeBasedData,
      };
    } else {
      const eventBasedData = buildEventBasedTrigger(formData);
      return {
        ...eventBasedData,
      };
    }
  };

  const buildTimeBasedTrigger = (
    formData: z.infer<typeof workflowTriggerSchema>
  ) => {
    if (!formData.date || !formData.time) {
      toast({
        title: "Error",
        description: "Missing date or time",
        variant: "destructive",
      });
      return null;
    }

    try {
      const formattedDate = formatDateString(formData.date);
      const formattedTime = formatTimeString(formData.time);
      const startDateISO = new Date(
        `${formattedDate}T${formattedTime}:00`
      ).toISOString();

      return {
        triggerType: 1,
        startDate: startDateISO,
      };
    } catch (dateError) {
      toast({
        title: "Error",
        description: "Invalid date format",
        variant: "destructive",
      });
      return null;
    }
  };

  const buildEventBasedTrigger = (
    formData: z.infer<typeof workflowTriggerSchema>
  ) => {
    return {
      triggerType: 0,
      eventType: formData.eventType,
    };
  };

  const formatDateString = (dateStr: string): string => {
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
      const parts = dateStr.split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const formatTimeString = (timeStr: string): string => {
    return timeStr && timeStr.length === 5 ? timeStr : "00:00";
  };

  const updateTriggerAPI = useCallback(
    async (formData: z.infer<typeof workflowTriggerSchema>) => {
      try {
        const workflowId = getWorkflowId();
        if (!workflowId) {
          toast({
            title: "Error",
            description: "No workflow ID found",
            variant: "destructive",
          });
          return { success: false, error: "Missing workflow ID" };
        }

        const triggerData = buildTriggerData(formData);
        if (!triggerData) {
          return { success: false, error: "Invalid trigger configuration" };
        }

        await updateTriggerMutation.mutateAsync({
          workflowId: parseInt(workflowId.toString()),
          triggerData,
        });

        const title = isEditMode ? "Trigger Updated" : "Trigger Configured";
        const description = "Workflow trigger configured successfully.";
        toast({ title, description });

        return { success: true };
      } catch (error: any) {
        const errorMessage = error?.message || "Failed to save trigger data";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [
      existingWorkflow?.id,
      createdWorkOrderID,
      stepHelpers,
      updateTriggerMutation,
      toast,
      isEditMode,
    ]
  );

  // ✅ FIX 4: Form submission - SAME PATTERN AS NewList
  const handleFormSubmit = useCallback(
    async (formData: z.infer<typeof workflowTriggerSchema>) => {
      try {
        const isValid = await FormService.validateForm(workflowTriggerForm);
        if (!isValid) {
          toast({
            title: "Please complete all required trigger configuration.",
            description: "Check if all fields are properly filled",
            variant: "destructive",
          });
          return;
        }

        let workflowId = existingWorkflow?.id || createdWorkOrderID;

        if (!workflowId && stepHelpers) {
          try {
            const step0Data = stepHelpers.getStepData(0);
            workflowId = step0Data?.workflowId;
          } catch (e) {
            toast({
              title: "Error",
              description: e?.message,
              variant: "destructive",
            });
          }
        }

        if (!workflowId) {
          try {
            const storageKey = "workflow-builder-progress";
            const progressData = localStorage.getItem(storageKey);
            if (progressData) {
              const parsed = JSON.parse(progressData);
              workflowId = parsed.stepData?.[0]?.workflowId;
            }
          } catch (e) {
            toast({
              title: "Error",
              description: e?.message,
              variant: "destructive",
            });
          }
        }

        const dataToSave = {
          ...formData,
          workflowId,
          timestamp: new Date().toISOString(),
        };

        // ✅ Save step data BEFORE calling onNext - SAME AS NewList
        saveStepData(dataToSave);

        if (onNext) {
          onNext();
        }
      } catch (error) {
        toast({
          title: "Please check your trigger configuration and try again.",
          variant: "destructive",
        });
      }
    },
    [
      saveStepData,
      onNext,
      toast,
      createdWorkOrderID,
      workflowTriggerForm,
      existingWorkflow?.id,
      stepHelpers,
    ]
  );

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    const handleNextClick = async () => {
      try {
        const isValid = await FormService.validateForm(workflowTriggerForm);
        if (!isValid) {
          toast({
            title: "Please complete all required trigger configuration.",
            variant: "destructive",
          });
          return;
        }

        const formData = workflowTriggerForm.getValues();

        let workflowId = existingWorkflow?.id || createdWorkOrderID;

        if (!workflowId && stepHelpers) {
          try {
            const step0Data = stepHelpers.getStepData(0);
            workflowId = step0Data?.workflowId;
          } catch (e) {
            toast({
              title: "Error",
              description: e?.message,
              variant: "destructive",
            });
          }
        }

        if (!workflowId) {
          try {
            const storageKey = "workflow-builder-progress";
            const progressData = localStorage.getItem(storageKey);
            if (progressData) {
              const parsed = JSON.parse(progressData);
              workflowId = parsed.stepData?.[0]?.workflowId;
            }
          } catch (e) {
            toast({
              title: "Error",
              description: e?.message,
              variant: "destructive",
            });
          }
        }

        if (!workflowId) {
          toast({
            title: "Missing Workflow",
            description: "Please complete workflow details first.",
            variant: "destructive",
          });
          return;
        }

        // ✅ Call API first - SAME AS NewList pattern
        const result = await updateTriggerAPI(formData);
        if (!result.success) {
          return;
        }

        const dataToSave = {
          ...formData,
          workflowId,
          timestamp: new Date().toISOString(),
        };

        // ✅ Save step data AFTER API success - SAME AS NewList
        saveStepData(dataToSave);

        if (onNext) {
          onNext();
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Please check your configuration and try again.",
          variant: "destructive",
        });
      }
    };

    return (
      <div className="pt-6 flex justify-between border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onPrevious}
          type="button"
          className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={handleNextClick}
          disabled={isLoadingEventTypes || updateTriggerMutation.isPending}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {updateTriggerMutation.isPending ? "Saving..." : "Next"}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }, [
    readOnly,
    onPrevious,
    isLoadingEventTypes,
    updateTriggerMutation.isPending,
    workflowTriggerForm,
    toast,
    saveStepData,
    onNext,
    existingWorkflow?.id,
    stepHelpers,
    updateTriggerAPI,
  ]);

  return (
    <div className="space-y-6 pt-4">
      <DynamicForm
        fields={workflowTriggerFormFields}
        form={workflowTriggerForm}
        onSubmit={handleFormSubmit}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "space-y-6",
            button: "px-4 py-2 rounded-md font-medium transition-colors",
            buttonContainer: "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            textarea:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
            label: "block text-sm font-medium text-gray-700 mb-1",
            error: "text-sm text-red-600 mt-1",
            container: "space-y-1",
          },
        }}
        gridColumns={6}
        responsiveColumns={{ sm: 1, md: 1 }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />
    </div>
  );
};

export default WorkflowTriggerStep;
